import type { Media, SetLogRecord, WorkoutSession, WorkoutSessionItem, MovementPattern, ExerciseEquipment } from '../../../types';

/**
 * Card types for the workout player
 */
export type WorkoutCardType =
  | 'exercise_info'
  | 'media'
  | 'cues'
  | 'set'
  | 'rest';

export interface BaseWorkoutCard {
  id: string;
  type: WorkoutCardType;
  itemId: string; // Reference to the workout item
  exerciseName: string;
  index: number; // Position in the overall playlist
  autoDuration?: number; // Auto-advance duration in seconds
  isSkipped?: boolean; // Whether this exercise has been skipped
}

export interface ExerciseInfoCard extends BaseWorkoutCard {
  type: 'exercise_info';
  description?: string;
  howTo?: string;
  setsPlanned: number;
  repsPlanned: number;
  restSecPlanned: number;
  isSwapped?: boolean;
  movementPattern?: MovementPattern;
  equipmentTags?: ExerciseEquipment[];
}

export interface MediaCard extends BaseWorkoutCard {
  type: 'media';
  media: Media;
  mediaIndex: number; // Which media item this is for the exercise
}

export interface CuesCard extends BaseWorkoutCard {
  type: 'cues';
  cues?: string;
}

export interface SetCard extends BaseWorkoutCard {
  type: 'set';
  setIndex: number; // 1-indexed
  set: {
    index: number;
    weight?: number;
    repsDone?: number;
    notes?: string;
  };
  repsPlanned: number;
  lastWeightSuggestion?: number;
  recentLogs: SetLogRecord[];
}

export interface RestCard extends BaseWorkoutCard {
  type: 'rest';
  restSeconds: number;
  afterSet: number; // Which set this rest follows
  movementPattern?: MovementPattern; // For recommended rest time calculation
}

export type WorkoutCard =
  | ExerciseInfoCard
  | MediaCard
  | CuesCard
  | SetCard
  | RestCard;

/**
 * Returns true if the string looks like a raw Firestore document ID (UUID)
 * rather than a human-readable exercise name.
 * Firestore auto-IDs are 20-char alphanumeric strings; RFC-4122 UUIDs follow
 * the 8-4-4-4-12 hex pattern.  Either form in exerciseName indicates the field
 * was incorrectly populated with the document ID instead of the display name.
 */
function looksLikeId(value: string): boolean {
  // RFC-4122 UUID: xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx
  if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value)) {
    return true;
  }
  // Firestore auto-ID: exactly 20 alphanumeric characters
  if (/^[A-Za-z0-9]{20}$/.test(value)) {
    return true;
  }
  return false;
}

/**
 * Resolve a human-readable exercise name from a snapshot item.
 * Falls back to 'Exercise' when the field is absent, empty, or contains a
 * raw document ID that was mistakenly stored in place of the display name.
 */
function resolveExerciseName(item: WorkoutSessionItem): string {
  const name = item.exerciseName;
  if (!name || looksLikeId(name)) {
    return 'Exercise';
  }
  return name;
}

/**
 * Generates a flat playlist of workout cards from session data
 */
export function generateWorkoutPlaylist(
  session: WorkoutSession,
  logHistory: Record<string, SetLogRecord[]>
): WorkoutCard[] {
  const cards: WorkoutCard[] = [];
  let cardIndex = 0;

  session.workoutSnapshot.items.forEach((item: WorkoutSessionItem, itemIdx: number) => {
    const itemLogs = logHistory[item.id] || [];
    const lastWeight = itemLogs.find(log => typeof log.weight === 'number')?.weight;
    const isSkipped = item.skipped === true;
    // Resolve the display name once per item so every card for this exercise
    // shows a consistent, human-readable label even on older session documents
    // where exerciseName was missing or accidentally stored as a document ID.
    const exerciseName = resolveExerciseName(item);

    // 1. Exercise Info Card
    cards.push({
      id: `${item.id}-info`,
      type: 'exercise_info',
      itemId: item.id,
      exerciseName,
      description: item.exerciseDescription,
      howTo: item.howTo,
      setsPlanned: item.setsPlanned,
      repsPlanned: item.repsPlanned,
      restSecPlanned: item.restSecPlanned || 60,
      isSwapped: !!item.exerciseSwap,
      movementPattern: item.movementPattern,
      equipmentTags: item.equipmentTags,
      index: cardIndex++,
      autoDuration: 5, // Quick glance at exercise overview
      isSkipped,
    });

    // 2. Media Cards (one per media item) - no duration since these are optional reference
    if (item.media && item.media.length > 0) {
      item.media.forEach((media, mediaIdx) => {
        cards.push({
          id: `${item.id}-media-${mediaIdx}`,
          type: 'media',
          itemId: item.id,
          exerciseName,
          media,
          mediaIndex: mediaIdx,
          index: cardIndex++,
          autoDuration: 0, // Optional refresher, not counted in estimate
          isSkipped,
        });
      });
    }

    // 3. Cues Card (if cues exist)
    const cues = item.customCues || item.defaultCues;
    if (cues) {
      cards.push({
        id: `${item.id}-cues`,
        type: 'cues',
        itemId: item.id,
        exerciseName,
        cues,
        index: cardIndex++,
        autoDuration: 10, // Quick read of key cues
        isSkipped,
      });
    }

    // 4. Set Cards (with Rest Cards between them)
    // Set duration formula: ~4s per rep + 15s setup/transition
    const setDuration = (item.repsPlanned * 4) + 15;

    item.sets.forEach((set, setIdx) => {
      cards.push({
        id: `${item.id}-set-${set.index}`,
        type: 'set',
        itemId: item.id,
        exerciseName,
        setIndex: set.index,
        set,
        repsPlanned: item.repsPlanned,
        lastWeightSuggestion: lastWeight,
        recentLogs: itemLogs,
        index: cardIndex++,
        autoDuration: setDuration,
        isSkipped,
      });

      // Add rest card after each set except the last set of the last exercise
      const isLastSet = setIdx === item.sets.length - 1;
      const isLastExercise = itemIdx === session.workoutSnapshot.items.length - 1;

      if (!isLastSet || !isLastExercise) {
        cards.push({
          id: `${item.id}-rest-${set.index}`,
          type: 'rest',
          itemId: item.id,
          exerciseName,
          restSeconds: item.restSecPlanned || 60,
          afterSet: set.index,
          movementPattern: item.movementPattern, // Pass through for recommended rest calculation
          index: cardIndex++,
          autoDuration: item.restSecPlanned || 60,
          isSkipped,
        });
      }
    });
  });

  return cards;
}

/**
 * Gets a display label for a card (for playlist view)
 */
export function getCardLabel(card: WorkoutCard): string {
  switch (card.type) {
    case 'exercise_info':
      return `${card.exerciseName} - Info`;
    case 'media':
      return `${card.exerciseName} - Media ${card.mediaIndex + 1}`;
    case 'cues':
      return `${card.exerciseName} - Cues`;
    case 'set':
      return `${card.exerciseName} - Set ${card.setIndex}`;
    case 'rest':
      return `${card.exerciseName} - Rest`;
    default:
      return 'Unknown';
  }
}

/**
 * Gets text content for text-to-speech
 */
export function getCardSpeechText(card: WorkoutCard): string {
  switch (card.type) {
    case 'exercise_info':
      return `Next exercise: ${card.exerciseName}. ${card.setsPlanned} sets of ${card.repsPlanned} reps. ${card.description || ''}`;
    case 'media':
      return `Reference media for ${card.exerciseName}`;
    case 'cues':
      return `Cues for ${card.exerciseName}: ${card.cues || ''}`;
    case 'set':
      return `Set ${card.setIndex} of ${card.exerciseName}. ${card.repsPlanned} reps.`;
    case 'rest':
      return `Rest for ${card.restSeconds} seconds.`;
    default:
      return '';
  }
}
